import { useTranslation } from "react-i18next";
import { Col, Row, theme, Typography } from "antd";
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import { Link, useParams } from "react-router-dom";
import _ from "lodash";

import { useGetTopicsQuery } from "../../../redux/service.js";
import DiscussionTools from "./DiscussionTools.jsx";
import UserAvatar from "../../../domains/users/components/UserAvatar.tsx";
import CommentsIcon from "@/assets/icons/comments.svg?react";
import PinIcon from "@/assets/icons/pin-icon.svg?react";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";

const { Title, Text } = Typography;

const DiscussionBox = ({ discussion, showAvatar = false }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const parser = new DOMParser();
  const { topic } = useParams();

  const { data: discussionTopics } = useGetTopicsQuery({ pagination: "off" });

  const getCreationDate = () => {
    if (
      DateTime.fromISO(discussion.createdAt).diff(DateTime.now(), "days").days >
      2
    ) {
      return DateTime.fromISO(discussion.createdAt).toRelative();
    } else {
      return DateTime.fromISO(discussion.createdAt).toFormat("dd MMM yyyy");
    }
  };

  return (
    <Row
      className="my-3 py-4"
      style={{ borderTop: "1px solid rgba(1, 1, 46, 0.134)" }}
    >
      {showAvatar && (
        <Col span={1}>
          <UserAvatar user={discussion.createdBy} />
        </Col>
      )}
      <Col span={19} className="ps-3 pe-3">
        <Link to={`/discussions/${discussion.id}`}>
          <Title
            className="mb-2"
            level={4}
            style={{
              color: token.colorTextThird,
              fontWeight: "bold",
              textDecoration: "underline",
            }}
          >
            {_.truncate(discussion.title, { length: 200 })}
          </Title>
        </Link>
        <Text style={{ color: "rgba(139, 141, 152, 1)" }}>
          {_.truncate(
            parser.parseFromString(discussion.content, "text/html").body
              .textContent,
            { length: 500 },
          )}
        </Text>
        <div style={{ display: "flex" }}>
          <div>
            <Text
              style={{ display: "block", color: "rgba(139, 141, 152, 1)" }}
              className="mt-2"
            >
              {getCreationDate()} {t("by") + " "}
              <Link to={`/users/${discussion.createdBy.id}`}>
                {discussion.createdBy.fullName}
              </Link>{" "}
              {discussionTopics ? (
                <>
                  {t("in") + " "}
                  {discussionTopics
                    .filter((i) => discussion.topics.includes(i.id))
                    .map((i, index) => (
                      <span key={i.title}>
                        <Link key={i.title} to={`/community/topics/${i.title}`}>
                          {i.title}
                        </Link>
                        {index + 1 !== discussion.topics.length && ", "}
                      </span>
                    ))}
                </>
              ) : null}
            </Text>
          </div>
          <div className="ms-3" style={{ alignContent: "end" }}>
            <DiscussionTools
              discussion={discussion}
              listProps={{ width: "180px" }}
              iconProps={{
                style: {
                  fontSize: "20px",
                  transform: "rotate(90deg)",
                  color: "rgba(74, 105, 156, 1)",
                },
              }}
            />
          </div>
          {discussion.isPinned && topic ? (
            <div className="ms-3" style={{ alignContent: "end" }}>
              <BaseSvgRenderer component={PinIcon} />
              <Text className="ms-2" style={{ color: "rgba(39, 155, 64, 1)" }}>
                {t("pinnedDiscussion")}
              </Text>
            </div>
          ) : null}
        </div>
      </Col>
      <Col span={4}>
        {[
          {
            icon: (
              <span className="me-3">
                <BaseSvgRenderer component={CommentsIcon} />
              </span>
            ),
            text: t("commentsCount", {
              count: discussion.commentsCount,
              plural: discussion.commentsCount === 1 ? "" : "s",
            }),
          },
        ].map((i) => (
          <div
            key={i.text}
            style={{ display: "flex", justifyContent: "left" }}
            className="mb-2"
          >
            {i.icon}
            <Title
              level={5}
              style={{
                color: "rgba(74, 105, 156, 1)",
                fontWeight: 400,
              }}
            >
              {i.text}
            </Title>
          </div>
        ))}
      </Col>
    </Row>
  );
};

DiscussionBox.propTypes = {
  discussion: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    commentsCount: PropTypes.number.isRequired,
    views: PropTypes.number.isRequired,
    createdAt: PropTypes.string.isRequired,
    createdBy: PropTypes.object.isRequired,
    topics: PropTypes.array.isRequired,
    isPinned: PropTypes.bool.isRequired,
  }).isRequired,
  showAvatar: PropTypes.bool,
};

export default DiscussionBox;
